import upperFirst from 'lodash/upperFirst';
import { useTranslation } from 'next-i18next';
import { useState, createRef, useEffect, RefObject, isValidElement } from 'react';
import { BsChevronDown } from 'react-icons/bs';
import {
  PaymentMethod,
  PropertyPaymentMethod,
  SUPPORTED_CREDIT_CARD_BY_SBPS,
} from '../../../core/constants';
import testIds from './testIds';
import type { Hotel } from '../../../core/isValidHotel';
import SkipTranslationWrapper from '../../common/SkipTranslationWrapper';

type ListProps = {
  title: string;
  description: string | string[] | JSX.Element;
  testId: string;
};

const ListItem = ({ title, description, testId }: ListProps) => {
  if (isValidElement(description)) {
    return (
      <div
        data-testid={testId}
        className="flex flex-col sm:mb-2 sm:flex-row sm:gap-4 sm:border-t sm:pt-8"
      >
        <div className="border-gray-200 sm:w-1/4">
          <p
            data-testid={testIds.listitem_title}
            className="mb-3 text-large font-bold sm:font-normal"
          >
            {title}
          </p>
        </div>
        <div className="border-gray-200 sm:flex sm:flex-1">
          <div className="sm:flex sm:flex-row sm:flex-wrap">
            <div
              className="mb-6 hidden sm:block"
              data-testid={testIds.listitem_description_desktop}
            >
              {description}
            </div>
            <div className="mb-6 sm:hidden" data-testid={testIds.listitem_description_mobile}>
              {description}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const descriptionGrouped =
    typeof description === 'string'
      ? description.split('\n').map((text) => upperFirst(text))
      : Array.from(new Set(description as string[]));

  return (
    <div
      data-testid={testId}
      className="flex flex-col border-gray-200 sm:mb-2 sm:flex-row sm:gap-4 sm:border-t sm:pt-8"
    >
      <div className="sm:w-1/4">
        <p
          data-testid={testIds.listitem_title}
          className="mb-3 text-large font-bold sm:font-normal"
        >
          {title}
        </p>
      </div>
      <div className="sm:flex sm:flex-1">
        <div className="sm:flex sm:flex-1 sm:flex-row sm:flex-wrap">
          <div className="mb-6 hidden sm:block">
            {descriptionGrouped.map((x) => (
              <p
                data-testid={testIds.listitem_description_desktop}
                key={x}
                className="text-small leading-6 text-gray-600"
              >
                {x}
              </p>
            ))}
          </div>
          <div className="mb-6 sm:hidden">
            {descriptionGrouped.map((x) => (
              <p
                data-testid={testIds.listitem_description_mobile}
                key={x}
                className="text-small leading-6 text-gray-600"
              >
                {x}
              </p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

type PaymentItemsProps = {
  tabistPaymentTypes: Hotel['tabistPaymentTypes'];
  propertyPaymentTypes: Hotel['propertyPaymentTypes'];
  allowPayAtHotelForForeignGuests: Hotel['allowPayAtHotelForForeignGuests'];
  creditcard: Hotel['creditcard'];
};

const PaymentItems = ({
  tabistPaymentTypes,
  propertyPaymentTypes,
  allowPayAtHotelForForeignGuests,
  creditcard,
}: PaymentItemsProps) => {
  const { t } = useTranslation(['common', 'enum', 'hotel']);
  const sortedPaymentOption = [
    PropertyPaymentMethod.CASH,
    PaymentMethod.PAYPAY,
    PaymentMethod.CREDIT_CARD,
    PaymentMethod.CREDIT_CARD2,
  ];
  const onsitePayOptions = propertyPaymentTypes?.length
    ? (propertyPaymentTypes as unknown as PaymentMethod[])
        .slice()
        .sort((a, b) => sortedPaymentOption.indexOf(a) - sortedPaymentOption.indexOf(b))
    : [];
  const prepayOptions = tabistPaymentTypes?.length
    ? tabistPaymentTypes
        .slice()
        .filter((type) => type !== PaymentMethod.PAY_AT_HOTEL)
        .filter((type) => {
          // Remove paypay_hivex payment option if the feature flag is disabled
          if (process.env.NEXT_PUBLIC_FEATURE_FLAG_HIVEX_PAYMENT !== 'true') {
            return type !== PaymentMethod.PAYPAY_HIVEX;
          }
          return true;
        })
        .filter((type, i, self) => {
          // Remove duplicated payment options (e.g. paypay and paypay_hivex are shown as one option)
          return i === self.findIndex((s) => s.includes(type) || type.includes(s));
        })
        .sort((a, b) => sortedPaymentOption.indexOf(a) - sortedPaymentOption.indexOf(b))
    : [];
  const isCreditCardPayment = (paymentOption: PaymentMethod) => {
    return [PaymentMethod.CREDIT_CARD, PaymentMethod.CREDIT_CARD2].includes(paymentOption);
  };

  return !!onsitePayOptions?.length ||
    !!prepayOptions?.length ||
    (tabistPaymentTypes?.includes(PaymentMethod.PAY_AT_HOTEL) &&
      !allowPayAtHotelForForeignGuests) ? (
    <ListItem
      testId={testIds.payment}
      title={t('hotel:policy.payment')}
      description={
        <div className="text-gray-600">
          <div className="grid grid-cols-1 gap-2 text-small  leading-6 md:grid-cols-2">
            {!!onsitePayOptions?.length && (
              <div>
                <p className="font-bold">{t('hotel:policy.payment.on_site_payment_options')}</p>
                <ul className="list-disc pl-4">
                  {onsitePayOptions?.map((onsitePayOption) => (
                    <li key={onsitePayOption}>
                      {t(`enum:PropertyPaymentType.${onsitePayOption}`)}
                      <div>
                        {isCreditCardPayment(onsitePayOption) &&
                          creditcard &&
                          (creditcard.split(' / ') ?? []).map((card, index) => (
                            <>
                              <p className="inline" key={card}>
                                {card}
                              </p>
                              <SkipTranslationWrapper tag="span">
                                {index < (creditcard.split(' / ') ?? []).length - 1 ? ', ' : ''}
                              </SkipTranslationWrapper>
                            </>
                          ))}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {!!prepayOptions.length && (
              <div data-testid={testIds.prepay}>
                <p className="font-bold">{t('hotel:policy.payment.pre_pay_options')}</p>
                <ul className="list-disc pl-4">
                  {prepayOptions?.map((prepayOption) => {
                    return (
                      <li key={prepayOption}>
                        {t(`enum:TabistPaymentType.${prepayOption}`)}
                        <div>
                          {isCreditCardPayment(prepayOption) &&
                            SUPPORTED_CREDIT_CARD_BY_SBPS.map((card, index) => (
                              <>
                                <p className="inline" key={card}>
                                  {card}
                                </p>
                                <SkipTranslationWrapper tag="span">
                                  {index < SUPPORTED_CREDIT_CARD_BY_SBPS.length - 1 ? ', ' : ''}
                                </SkipTranslationWrapper>
                              </>
                            ))}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
          {tabistPaymentTypes?.includes(PaymentMethod.PAY_AT_HOTEL) &&
            !allowPayAtHotelForForeignGuests && (
              <p className="pt-4 text-small">
                {t('hotel:policy.payment.require_pre_pay_for_foreign_guest')}
              </p>
            )}
        </div>
      }
    />
  ) : null;
};

type Props = {
  cancellationPolicy: string;
  tabistPaymentTypes: Hotel['tabistPaymentTypes'];
  propertyPaymentTypes: Hotel['propertyPaymentTypes'];
  allowPayAtHotelForForeignGuests: Hotel['allowPayAtHotelForForeignGuests'];
  creditcard: Hotel['creditcard'];
  precautions: Hotel['precautions'];
  noShowPolicy: Hotel['noShowPolicy'];
};

const useRefDimensions = (ref: RefObject<HTMLDivElement>) => {
  const [clientHeight, setClientHeight] = useState(ref.current?.clientHeight || 0);
  useEffect(() => {
    if (ref.current) {
      const { current } = ref;
      if (clientHeight !== current.clientHeight) {
        setClientHeight(current.clientHeight);
      }
    }
  }, [ref, clientHeight]);
  return clientHeight;
};

const TAILWIND_MAX_H_112_SIZE = 448;

const Policy = ({
  cancellationPolicy,
  tabistPaymentTypes,
  propertyPaymentTypes,
  allowPayAtHotelForForeignGuests,
  creditcard,
  precautions,
  noShowPolicy,
}: Props) => {
  const { t } = useTranslation(['common', 'enum', 'hotel']);
  const [collapsed, setCollapsed] = useState(false);
  const divRef = createRef<HTMLDivElement>();
  const clientHeight = useRefDimensions(divRef);
  const needsToBeCollapsed = clientHeight >= TAILWIND_MAX_H_112_SIZE;

  return (
    <>
      <h3 className="app-section-title pb-4">{t('hotel:policy.title')}</h3>
      <div
        className={`overflow-hidden ${
          needsToBeCollapsed && collapsed ? '' : 'max-h-112'
        } rounded-t-md bg-white sm:max-h-full`}
        ref={divRef}
      >
        {!!cancellationPolicy && (
          <ListItem
            testId={testIds.cancellationPolicy}
            title={t('hotel:policy.cancel_policy')}
            description={cancellationPolicy}
          />
        )}
        <PaymentItems
          tabistPaymentTypes={tabistPaymentTypes}
          propertyPaymentTypes={propertyPaymentTypes}
          allowPayAtHotelForForeignGuests={allowPayAtHotelForForeignGuests}
          creditcard={creditcard}
        />
        {!!precautions && (
          <ListItem
            testId={testIds.precautions}
            title={t('hotel:policy.pre_cautions')}
            description={precautions}
          />
        )}
        {!!noShowPolicy && (
          <ListItem
            testId={testIds.noShowPolicy}
            title={t('hotel:policy.no_show_policy')}
            description={noShowPolicy}
          />
        )}
      </div>
      {needsToBeCollapsed && (
        <div className="flex w-full flex-col rounded-b-md bg-white sm:hidden">
          <button
            type="button"
            className="flex place-content-center py-4 text-orange-700 focus:outline-none"
            onClick={() => {
              setCollapsed(!collapsed);
            }}
          >
            <span>{collapsed ? t('common:button.close') : t('common:button.show_more')}</span>{' '}
            <BsChevronDown
              className={`ml-4 self-center duration-200${collapsed ? 'rotate-180' : ''}`}
            />
          </button>
        </div>
      )}
    </>
  );
};

export default Policy;
